import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { AppButton } from '../../components/AppButton';
import { CalendarDatePicker } from '../../components/CalendarDatePicker';
import { ProjectMemberPicker } from '../../components/ProjectMemberPicker';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createProjectSubtask,
  getProjectTask,
  moveProjectTask,
  reorderProjectSubtasks,
  updateProjectSubtask,
  updateProjectTask,
} from '../../services/projectService';
import type {
  ProjectColumn,
  ProjectMember,
  ProjectSubtask,
  ProjectTask,
} from '../../types/project';
import { formatCalendarDate, todayBusinessCalendarDate } from '../../utils/dateTime';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  formatDurationCompact,
  formatDurationInput,
  planTaskMove,
  validateCreateTaskForm,
  type TaskFormFieldErrors,
} from '../../utils/projectTaskForm';
import {
  FOCUS_TIMER_PRESETS,
  type FocusTimerMode,
} from '../../utils/taskFocusTimer';
import type { TaskFocusTimerView } from '../../hooks/useTaskFocusTimer';
import { CompleteTaskModal } from './CompleteTaskModal';
import { TaskTimeLogView } from './TaskTimeLogView';
import { runOperation } from '../../utils/operationFeedback';

type Props = {
  projectId: number;
  taskId: number;
  members: ProjectMember[];
  columns: ProjectColumn[];
  canEdit: boolean;
  initialTask?: ProjectTask | null;
  onClose: () => void;
  onTaskChanged?: () => void;
  activeTimer: TaskFocusTimerView | null;
  onStartTimer: (mode: FocusTimerMode) => void;
  onStopTimer: () => void;
  /** Changes when a focus session is logged, so time spent is reloaded. */
  timeLogVersion?: number;
};

type Editor = 'due' | 'members' | 'move' | 'timer' | null;

export function TaskDetailsPanel({
  projectId,
  taskId,
  members,
  columns,
  canEdit,
  initialTask,
  onClose,
  onTaskChanged,
  activeTimer,
  onStartTimer,
  onStopTimer,
  timeLogVersion = 0,
}: Props) {
  const palette = useAppColors();
  const [task, setTask] = useState<ProjectTask | null>(initialTask ?? null);
  const [taskName, setTaskName] = useState(initialTask?.taskName ?? '');
  const [assignedEmployeeIds, setAssignedEmployeeIds] = useState<number[]>(
    initialTask?.assignees.map(item => item.employeeId) ?? [],
  );
  const [dueDate, setDueDate] = useState(initialTask?.dueDate ?? '');
  const [timeEstimate, setTimeEstimate] = useState(
    formatDurationInput(initialTask?.timeEstimateMinutes),
  );
  const [timeSpent, setTimeSpent] = useState(
    formatDurationInput(initialTask?.timeSpentMinutes) || '00:00',
  );
  const [description, setDescription] = useState(initialTask?.description ?? '');
  const [newSubtaskName, setNewSubtaskName] = useState('');
  const [newSubtaskMemberIds, setNewSubtaskMemberIds] = useState<number[]>([]);
  const [subtaskDrafts, setSubtaskDrafts] = useState<
    Record<number, { name: string; assignedEmployeeIds: number[] }>
  >({});
  const [subtaskMenuId, setSubtaskMenuId] = useState<number | null>(null);
  const [fieldErrors, setFieldErrors] = useState<TaskFormFieldErrors>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState<Editor>(null);
  const [completeMove, setCompleteMove] = useState<ProjectColumn | null>(null);
  const [showTimeLog, setShowTimeLog] = useState(false);
  const [draggingSubtaskId, setDraggingSubtaskId] = useState<number | null>(null);
  const [dragY, setDragY] = useState(0);
  const subtaskBoxes = useRef(new Map<number, { y: number; height: number }>());

  const themed = useMemo(
    () => ({
      card: {
        backgroundColor: palette.surface,
        borderColor: palette.border,
        borderWidth: 1,
        borderRadius: 16,
        flex: 1,
        minWidth: 280,
        maxWidth: 720,
      },
      title: {
        ...typography.screenTitle,
        color: palette.textPrimary,
        fontSize: 24,
        lineHeight: 32,
      },
      meta: { ...typography.caption, color: palette.textSecondary },
      propertyLabel: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      propertyValue: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      propertyMuted: { ...typography.body, color: palette.textMuted, fontWeight: '600' as const },
      section: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      error: { ...typography.caption, color: palette.error },
      hint: { ...typography.caption, color: palette.textSecondary },
      subtaskName: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      railLabel: { ...typography.caption, color: palette.textSecondary, fontWeight: '600' as const },
      menu: {
        borderWidth: 1,
        borderColor: palette.border,
        backgroundColor: palette.surface,
        borderRadius: 10,
        paddingVertical: spacing.xs,
        minWidth: 180,
      },
    }),
    [palette],
  );

  function applyTask(next: ProjectTask, notify = false) {
    setTask(next);
    setTaskName(next.taskName);
    setAssignedEmployeeIds(next.assignees.map(item => item.employeeId));
    setDueDate(next.dueDate ?? '');
    setTimeEstimate(formatDurationInput(next.timeEstimateMinutes));
    setTimeSpent(formatDurationInput(next.timeSpentMinutes) || '00:00');
    setDescription(next.description ?? '');
    setSubtaskDrafts(
      Object.fromEntries(
        next.subtasks
          .filter(item => item.subtaskId != null)
          .map(item => [
            item.subtaskId as number,
            {
              name: item.subtaskName,
              assignedEmployeeIds: item.assignees.map(assignee => assignee.employeeId),
            },
          ]),
      ),
    );
    if (notify) onTaskChanged?.();
  }

  const loadTask = useCallback(async () => {
    try {
      applyTask(await getProjectTask(projectId, taskId));
      setError('');
    } catch (caught) {
      if (!initialTask) {
        setError(projectErrorMessage(caught, 'Unable to load the task.'));
      }
    }
  }, [initialTask, projectId, taskId]);

  useEffect(() => {
    void loadTask();
  }, [loadTask]);

  useEffect(() => {
    if (timeLogVersion) void loadTask();
    // Only a newly logged focus session should trigger this reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLogVersion]);

  const column = columns.find(item => item.projectColumnId === task?.projectColumnId);
  const requiresDetails = column?.requiresTaskDetails === true;
  const timerForThisTask = activeTimer?.taskId === taskId ? activeTimer : null;

  async function persist(overrides: {
    taskName?: string;
    assignedEmployeeIds?: number[];
    dueDate?: string;
    timeEstimate?: string;
    timeSpent?: string;
    description?: string;
  } = {}) {
    if (!canEdit || busy || !column) return false;
    const nextName = overrides.taskName ?? taskName;
    const nextMembers = overrides.assignedEmployeeIds ?? assignedEmployeeIds;
    const nextDue = overrides.dueDate ?? dueDate;
    const nextEstimate = overrides.timeEstimate ?? timeEstimate;
    const nextSpent = overrides.timeSpent ?? timeSpent;
    const nextDescription = overrides.description ?? description;
    const result = validateCreateTaskForm({
      taskName: nextName,
      assignedEmployeeIds: nextMembers,
      dueDate: nextDue,
      timeEstimate: nextEstimate,
      timeSpent: nextSpent,
      description: nextDescription,
      descriptionOpen: true,
      subtasks: [],
      requiresTaskDetails: requiresDetails,
      columnName: column.columnName,
    });
    setFieldErrors(result.fieldErrors);
    const payload = result.payload;
    if (!payload) return false;
    const completedDate = task?.completedDate ?? null;
    setBusy(true);
    setError('');
    try {
      const next = await runOperation('update', () => updateProjectTask(projectId, taskId, {
        taskName: payload.taskName,
        assignedEmployeeIds: payload.assignedEmployeeIds ?? [],
        dueDate: payload.dueDate,
        completedDate,
        timeEstimate: payload.timeEstimate,
        timeSpent: payload.timeSpent,
        description: payload.description,
      }));
      applyTask(next, true);
      setEditor(null);
      return true;
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to save the task.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addSubtask() {
    const name = newSubtaskName.trim();
    if (!canEdit || busy || !name) {
      if (!name) {
        setFieldErrors(current => ({
          ...current,
          subtasks: 'Subtask name cannot be empty.',
        }));
      }
      return;
    }
    setBusy(true);
    setError('');
    try {
      const next = await runOperation('add', () => createProjectSubtask(projectId, taskId, {
        subtaskName: name,
        assignedEmployeeIds: newSubtaskMemberIds,
      }));
      applyTask(next, true);
      setNewSubtaskName('');
      setNewSubtaskMemberIds([]);
      setFieldErrors(current => ({ ...current, subtasks: undefined }));
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to add the subtask.'));
    } finally {
      setBusy(false);
    }
  }

  async function saveSubtask(subtask: ProjectSubtask, isCompleted = subtask.isCompleted) {
    if (!canEdit || busy || subtask.subtaskId == null) return;
    const draft = subtaskDrafts[subtask.subtaskId];
    const name = (draft?.name ?? subtask.subtaskName).trim();
    if (!name) {
      setFieldErrors(current => ({ ...current, subtasks: 'Subtask name cannot be empty.' }));
      return;
    }
    const subtaskId = subtask.subtaskId;
    setBusy(true);
    setError('');
    try {
      applyTask(
        await runOperation('update', () => updateProjectSubtask(projectId, subtaskId, {
          subtaskName: name,
          assignedEmployeeIds: draft?.assignedEmployeeIds ?? [],
          isCompleted,
        })),
        true,
      );
      setSubtaskMenuId(null);
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to save the subtask.'));
    } finally {
      setBusy(false);
    }
  }

  function dropSubtask(pageY: number) {
    const sourceId = draggingSubtaskId;
    setDraggingSubtaskId(null);
    if (!canEdit || sourceId == null || !task) return;
    const ordered = [...task.subtasks]
      .filter(item => item.subtaskId != null)
      .sort((left, right) => left.displayOrder - right.displayOrder);
    const ids = ordered.map(item => item.subtaskId as number);
    const without = ids.filter(id => id !== sourceId);
    let insertAt = without.length;
    for (let index = 0; index < without.length; index += 1) {
      const box = subtaskBoxes.current.get(without[index]);
      if (!box) continue;
      if (pageY < box.y + box.height / 2) {
        insertAt = index;
        break;
      }
    }
    without.splice(insertAt, 0, sourceId);
    if (without.join(',') === ids.join(',')) return;
    void (async () => {
      setBusy(true);
      try {
        applyTask(
          await runOperation(
            'update',
            () => reorderProjectSubtasks(projectId, taskId, without),
            {
              failureDetail: caught =>
                projectErrorMessage(caught, 'The subtasks could not be reordered.'),
            },
          ),
          true,
        );
      } catch {
        // runOperation already showed the "Update failed" popup.
      } finally {
        setBusy(false);
      }
    })();
  }

  async function moveTo(target: ProjectColumn) {
    if (!canEdit || busy || !task || !target.projectColumnId) return;
    const plan = planTaskMove({
      currentColumnId: task.projectColumnId,
      column: {
        ...target,
        taskCount: target.taskCount ?? 0,
        taskLimit: target.taskLimit,
      },
      assignedEmployeeId: task.assignedEmployeeId,
      assignedEmployeeIds: assignedEmployeeIds,
      dueDate,
      timeEstimate,
    });
    if (plan.kind === 'noop') {
      setEditor(null);
      return;
    }
    if (plan.kind === 'blocked') {
      Alert.alert(plan.title, plan.message);
      return;
    }
    if (plan.kind === 'promptComplete') {
      setEditor(null);
      setCompleteMove(target);
      return;
    }
    setBusy(true);
    try {
      const projectColumnId = target.projectColumnId;
      applyTask(
        await runOperation(
          'update',
          () => moveProjectTask(projectId, taskId, projectColumnId, { completedDate: null }),
          {
            failureDetail: caught =>
              projectErrorMessage(caught, 'The task could not be moved.'),
          },
        ),
        true,
      );
      setEditor(null);
    } catch {
      // runOperation already showed the "Update failed" popup.
    } finally {
      setBusy(false);
    }
  }

  if (!task) {
    return (
      <View style={[themed.card, styles.emptyCard]}>
        <Text style={themed.hint}>{error || 'Loading task…'}</Text>
        <Pressable accessibilityRole="button" accessibilityLabel="Close" onPress={onClose}>
          <Text style={themed.meta}>Close</Text>
        </Pressable>
      </View>
    );
  }

  const spentLabel = formatDurationCompact(task.timeSpentMinutes) || '0m';
  const estimateLabel = formatDurationCompact(task.timeEstimateMinutes) || 'Add';
  const dueLabel = task.dueDate ? formatCalendarDate(task.dueDate) : 'Add';
  const memberLabel =
    task.assignees.map(item => item.employeeName).join(', ') ||
    task.assignedEmployeeName ||
    'Add';
  const completedCount = task.subtasks.filter(item => item.isCompleted).length;

  return (
    <View style={styles.shell}>
      <View
        style={themed.card}
        {...(Platform.OS === 'web'
          ? {
              onClick: (event: { stopPropagation?: () => void }) =>
                event.stopPropagation?.(),
            }
          : {})}
      >
        <View style={styles.cardHeader}>
          <View style={styles.flex}>
            <TextInput
              accessibilityLabel="Task Name"
              value={taskName}
              onChangeText={setTaskName}
              onEndEditing={() => {
                if (taskName.trim() !== task.taskName) void persist();
              }}
              editable={canEdit}
              placeholder="Task name"
              placeholderTextColor={palette.textMuted}
              style={themed.title}
            />
            <Text accessibilityLabel={`Column: ${column?.columnName ?? 'Task details'}`} style={themed.meta}>
              {column?.columnName ?? 'Task details'}
            </Text>
            {task.createdByName ? (
              <Text style={themed.meta}>Added by {task.createdByName}</Text>
            ) : null}
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={onClose}
            hitSlop={8}
          >
            <Text style={[themed.meta, styles.close]}>X</Text>
          </Pressable>
        </View>

        <ScrollView
          contentContainerStyle={styles.body}
          keyboardShouldPersistTaps="handled"
          scrollEnabled={draggingSubtaskId == null}
          onTouchMove={event => {
            if (draggingSubtaskId == null) return;
            setDragY(event.nativeEvent.pageY);
          }}
          onTouchEnd={event => {
            if (draggingSubtaskId == null) return;
            dropSubtask(event.nativeEvent.pageY);
          }}
        >
          {showTimeLog ? (
            <TaskTimeLogView
              key={timeLogVersion}
              projectId={projectId}
              taskId={taskId}
              taskName={task.taskName}
              canEdit={canEdit}
              existingMinutes={task.timeSpentMinutes}
              employeeId={task.assignedEmployeeId ?? task.assignees[0]?.employeeId}
              employeeName={
                task.assignedEmployeeName ?? task.assignees[0]?.employeeName
              }
              members={members}
              assignees={task.assignees}
              onBack={() => setShowTimeLog(false)}
              onChanged={minutes => {
                setTask(current =>
                  current ? { ...current, timeSpentMinutes: minutes } : current,
                );
                setTimeSpent(formatDurationInput(minutes) || '00:00');
                onTaskChanged?.();
              }}
            />
          ) : (
          <>
          <View style={styles.properties}>
            <PropertyChip
              label="Members"
              value={memberLabel}
              muted={!task.assignees.length}
              onPress={() => setEditor(current => (current === 'members' ? null : 'members'))}
            />
            <PropertyChip
              label="Due date"
              value={dueLabel}
              muted={!task.dueDate}
              danger={Boolean(
                task.dueDate &&
                  !column?.isCompletedColumn &&
                  (task.dueDate.slice(0, 10) < todayBusinessCalendarDate()),
              )}
              onPress={() => setEditor(current => (current === 'due' ? null : 'due'))}
            />
            <PropertyChip
              label="Time spent"
              value={spentLabel}
              underline
              onPress={() => setShowTimeLog(true)}
            />
            <DurationField
              label="Time estimate"
              value={timeEstimate}
              error={fieldErrors.timeEstimate}
              canEdit={canEdit}
              busy={busy}
              displayValue={estimateLabel}
              onChange={setTimeEstimate}
              onSave={() => {
                const current = formatDurationInput(task.timeEstimateMinutes);
                if (timeEstimate.trim() === current) return;
                void persist({ timeEstimate });
              }}
            />
          </View>

          {editor === 'members' ? (
            <View style={styles.editor}>
              <ProjectMemberPicker
                label={requiresDetails ? 'Members *' : 'Members'}
                members={members}
                selectedIds={assignedEmployeeIds}
                onChange={setAssignedEmployeeIds}
                error={fieldErrors.member}
              />
              {canEdit ? (
                <AppButton title="Save task" onPress={() => void persist()} loading={busy} />
              ) : null}
            </View>
          ) : null}
          {fieldErrors.dueDate && editor !== 'due' ? (
            <Text style={themed.error}>{fieldErrors.dueDate}</Text>
          ) : null}

          <TextInput
            accessibilityLabel="Description"
            value={description}
            onChangeText={setDescription}
            onEndEditing={() => {
              if ((description || '') !== (task.description || '')) void persist();
            }}
            placeholder="Add a description..."
            placeholderTextColor={palette.textMuted}
            editable={canEdit}
            multiline
            textAlignVertical="top"
            style={[
              styles.description,
              {
                borderColor: palette.border,
                color: palette.textPrimary,
              },
            ]}
          />

          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}

          <View style={styles.sectionHeader}>
            <Text style={themed.section}>
              Subtasks {completedCount} / {task.subtasks.length}
            </Text>
          </View>
          <Text style={themed.hint}>Long-press a subtask to drag and reorder it.</Text>

          {task.subtasks.map(subtask => {
            const id = subtask.subtaskId;
            if (id == null) return null;
            const draft = subtaskDrafts[id] ?? {
              name: subtask.subtaskName,
              assignedEmployeeIds: subtask.assignees.map(item => item.employeeId),
            };
            return (
              <View
                key={id}
                style={styles.subtaskRow}
                onLayout={event => {
                  if (Platform.OS === 'web') {
                    // react-native-web layout events carry the DOM node on
                    // nativeEvent.target and have no currentTarget.
                    const node = (event.nativeEvent as { target?: unknown })
                      .target as { getBoundingClientRect?: () => DOMRect } | undefined;
                    const rect = node?.getBoundingClientRect?.();
                    if (rect) subtaskBoxes.current.set(id, { y: rect.top, height: rect.height });
                    return;
                  }
                  event.currentTarget.measureInWindow((_x, y, _w, height) => {
                    subtaskBoxes.current.set(id, { y, height });
                  });
                }}
              >
                <Pressable
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: subtask.isCompleted }}
                  accessibilityLabel={`Mark ${subtask.subtaskName} ${
                    subtask.isCompleted ? 'incomplete' : 'complete'
                  }`}
                  onPress={() => void saveSubtask(subtask, !subtask.isCompleted)}
                  style={[
                    styles.checkbox,
                    {
                      borderColor: palette.border,
                      backgroundColor: subtask.isCompleted ? palette.primary : palette.surface,
                    },
                  ]}
                >
                  {subtask.isCompleted ? (
                    <Text style={[styles.checkMark, { color: palette.onPrimary }]}>✓</Text>
                  ) : null}
                </Pressable>
                <Pressable
                  style={styles.flex}
                  delayLongPress={280}
                  onLongPress={event => {
                    if (!canEdit) return;
                    setDraggingSubtaskId(id);
                    setDragY(event.nativeEvent.pageY);
                  }}
                >
                  <TextInput
                    accessibilityLabel="Subtask name"
                    value={draft.name}
                    onChangeText={value =>
                      setSubtaskDrafts(current => ({
                        ...current,
                        [id]: { ...draft, name: value },
                      }))
                    }
                    onEndEditing={() => void saveSubtask(subtask)}
                    editable={canEdit}
                    style={[
                      themed.subtaskName,
                      subtask.isCompleted
                        ? { textDecorationLine: 'line-through', color: palette.textMuted }
                        : null,
                    ]}
                  />
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`More actions for ${subtask.subtaskName}`}
                  onPress={() =>
                    setSubtaskMenuId(current => (current === id ? null : id))
                  }
                  hitSlop={8}
                >
                  <Text style={themed.meta}>···</Text>
                </Pressable>
                {subtaskMenuId === id ? (
                  <View style={[themed.menu, styles.subtaskMenu]}>
                    <Text style={[themed.hint, styles.menuPad]}>Assign</Text>
                    <ProjectMemberPicker
                      label="Subtask members"
                      members={members}
                      selectedIds={draft.assignedEmployeeIds}
                      onChange={ids =>
                        setSubtaskDrafts(current => ({
                          ...current,
                          [id]: { ...draft, assignedEmployeeIds: ids },
                        }))
                      }
                      accessibilityLabel={`Assign members to ${subtask.subtaskName}`}
                    />
                    {canEdit ? (
                      <AppButton
                        title="Save subtask"
                        onPress={() => void saveSubtask(subtask)}
                      />
                    ) : null}
                  </View>
                ) : null}
              </View>
            );
          })}

          {canEdit ? (
            <View style={styles.addSubtask}>
              <TextInput
                accessibilityLabel="New subtask"
                value={newSubtaskName}
                onChangeText={setNewSubtaskName}
                onSubmitEditing={() => void addSubtask()}
                placeholder="Add subtask..."
                placeholderTextColor={palette.textMuted}
                style={[styles.addSubtaskInput, { color: palette.textPrimary }]}
              />
              <ProjectMemberPicker
                label="Subtask members"
                members={members}
                selectedIds={newSubtaskMemberIds}
                onChange={setNewSubtaskMemberIds}
                accessibilityLabel="Assign members to new subtask"
              />
              <AppButton title="Add subtask" onPress={() => void addSubtask()} loading={busy} />
              {fieldErrors.subtasks ? (
                <Text style={themed.error}>{fieldErrors.subtasks}</Text>
              ) : null}
            </View>
          ) : null}
          </>
          )}
        </ScrollView>
      </View>

      <View style={styles.rail}>
        <RailButton
          label="Move"
          icon="→"
          active={editor === 'move'}
          onPress={() => setEditor(current => (current === 'move' ? null : 'move'))}
        />
        <RailButton
          label="Timer"
          icon="⏱"
          active={editor === 'timer' || Boolean(timerForThisTask)}
          onPress={() => setEditor(current => (current === 'timer' ? null : 'timer'))}
        />
        {editor === 'move' ? (
          <View style={[themed.menu, styles.railMenu]}>
            {columns
              .filter(item => item.projectColumnId != null)
              .map(item => (
                <Pressable
                  key={String(item.projectColumnId)}
                  accessibilityRole="button"
                  accessibilityLabel={`Move to ${item.columnName}`}
                  onPress={() => void moveTo(item)}
                  style={styles.menuItem}
                >
                  <Text style={themed.subtaskName}>{item.columnName}</Text>
                </Pressable>
              ))}
          </View>
        ) : null}
        {editor === 'timer' ? (
          <View style={[themed.menu, styles.railMenu]}>
            {(Object.keys(FOCUS_TIMER_PRESETS) as FocusTimerMode[]).map(mode => (
              <Pressable
                key={mode}
                accessibilityRole="button"
                accessibilityLabel={`Start ${FOCUS_TIMER_PRESETS[mode].label} ${FOCUS_TIMER_PRESETS[mode].ratioLabel} for ${task.taskName}`}
                onPress={() => {
                  onStartTimer(mode);
                  setEditor(null);
                }}
                style={styles.menuItem}
              >
                <View style={styles.timerMenuRow}>
                  <Text style={styles.timerMenuIcon}>
                    {FOCUS_TIMER_PRESETS[mode].icon}
                  </Text>
                  <Text style={themed.subtaskName}>
                    {FOCUS_TIMER_PRESETS[mode].label}{' '}
                    {FOCUS_TIMER_PRESETS[mode].ratioLabel}
                  </Text>
                </View>
              </Pressable>
            ))}
            {timerForThisTask ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Stop timer for ${task.taskName}`}
                onPress={() => {
                  onStopTimer();
                  setEditor(null);
                }}
                style={styles.menuItem}
              >
                <Text style={themed.hint}>
                  ✕ Stop {timerForThisTask.remainingLabel}
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </View>

      <CalendarDatePicker
        open={editor === 'due'}
        value={dueDate}
        onClose={() => setEditor(null)}
        onConfirm={value => {
          setDueDate(value);
          void persist({ dueDate: value });
        }}
      />
      <CompleteTaskModal
        visible={completeMove != null}
        projectId={projectId}
        column={
          completeMove
            ? {
                ...completeMove,
                taskCount: completeMove.taskCount ?? 0,
                tasks: [],
              }
            : null
        }
        members={members}
        task={task}
        onClose={() => setCompleteMove(null)}
        onCompleted={() => {
          setCompleteMove(null);
          void loadTask();
          onTaskChanged?.();
        }}
      />
      {draggingSubtaskId != null ? (
        <View pointerEvents="none" style={[styles.dragGhost, { top: dragY - 18 }]}>
          <Text style={[themed.subtaskName, { color: palette.primary }]}>
            {task.subtasks.find(item => item.subtaskId === draggingSubtaskId)?.subtaskName}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function PropertyChip({
  label,
  value,
  muted,
  danger,
  underline,
  onPress,
}: {
  label: string;
  value: string;
  muted?: boolean;
  danger?: boolean;
  underline?: boolean;
  onPress: () => void;
}) {
  const palette = useAppColors();
  function handlePress(event?: { stopPropagation?: () => void }) {
    event?.stopPropagation?.();
    onPress();
  }
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={event => handlePress(event)}
      {...(Platform.OS === 'web'
        ? {
            onClick: (event: { stopPropagation?: () => void }) => handlePress(event),
          }
        : {})}
      style={[
        styles.property,
        Platform.OS === 'web' ? { cursor: 'pointer' as const } : null,
      ]}
    >
      <Text style={[styles.propertyLabel, { color: palette.textMuted }]}>{label}</Text>
      <Text
        style={[
          styles.propertyValue,
          {
            color: danger
              ? palette.error
              : underline
                ? palette.primary
                : muted
                  ? palette.textMuted
                  : palette.textPrimary,
            textDecorationLine: underline ? 'underline' : 'none',
          },
        ]}
      >
        {value}
      </Text>
    </Pressable>
  );
}

function DurationField({
  label,
  value,
  displayValue,
  error,
  canEdit,
  busy,
  onChange,
  onSave,
}: {
  label: string;
  value: string;
  displayValue: string;
  error?: string;
  canEdit: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  onSave: () => void;
}) {
  const palette = useAppColors();
  return (
    <View style={styles.durationField}>
      <Text style={[styles.propertyLabel, { color: palette.textMuted }]}>{label}</Text>
      {canEdit ? (
        <TextInput
          accessibilityLabel={label}
          value={value}
          onChangeText={onChange}
          onEndEditing={onSave}
          placeholder="00:00"
          placeholderTextColor={palette.textMuted}
          autoCapitalize="none"
          keyboardType="numbers-and-punctuation"
          style={[
            styles.durationInput,
            {
              borderColor: error ? palette.error : palette.border,
              color: palette.textPrimary,
            },
          ]}
        />
      ) : (
        <Text
          accessibilityLabel={label}
          style={[styles.propertyValue, { color: palette.textPrimary }]}
        >
          {displayValue}
        </Text>
      )}
      {error ? (
        <Text accessibilityRole="alert" style={[styles.propertyLabel, { color: palette.error }]}>
          {error}
        </Text>
      ) : (
        <Text style={[styles.propertyLabel, { color: palette.textMuted }]}>hh:mm</Text>
      )}
      {canEdit ? (
        <AppButton
          title="Save"
          accessibilityLabel={`Save ${label.toLowerCase()}`}
          onPress={onSave}
          loading={busy}
        />
      ) : null}
    </View>
  );
}

function RailButton({
  label,
  icon,
  active,
  onPress,
}: {
  label: string;
  icon: string;
  active?: boolean;
  onPress: () => void;
}) {
  const palette = useAppColors();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={styles.railItem}
    >
      <View
        style={[
          styles.railCircle,
          {
            backgroundColor: palette.surface,
            borderColor: active ? palette.primary : palette.border,
          },
        ]}
      >
        <Text style={{ color: palette.textPrimary, fontWeight: '700' }}>{icon}</Text>
      </View>
      <Text style={[styles.railLabel, { color: palette.textSecondary }]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  shell: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    maxHeight: '100%',
    zIndex: 2,
  },
  emptyCard: {
    padding: spacing.xl,
    gap: spacing.md,
    minWidth: 280,
  },
  flex: { flex: 1 },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    gap: spacing.md,
  },
  close: { fontWeight: '700', fontSize: 18 },
  body: {
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing.xxl,
    paddingTop: spacing.md,
    gap: spacing.md,
  },
  properties: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  property: {
    minWidth: 110,
    gap: 2,
  },
  propertyLabel: { ...typography.caption, fontWeight: '600' },
  propertyValue: { ...typography.body, fontWeight: '700' },
  editor: { gap: spacing.sm },
  durationField: {
    minWidth: 140,
    flexGrow: 1,
    gap: 4,
  },
  durationInput: {
    ...typography.input,
    borderWidth: 1,
    borderRadius: 10,
    minHeight: 44,
    paddingHorizontal: spacing.md,
  },
  description: {
    ...typography.input,
    minHeight: 72,
    borderBottomWidth: 1,
    paddingVertical: spacing.sm,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  subtaskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    position: 'relative',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderWidth: 1,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkMark: { fontSize: 12, fontWeight: '700' },
  subtaskMenu: {
    position: 'absolute',
    right: 0,
    top: 28,
    zIndex: 4,
    padding: spacing.md,
    gap: spacing.sm,
  },
  addSubtask: { gap: spacing.sm, marginTop: spacing.sm },
  addSubtaskInput: { ...typography.input, minHeight: 40 },
  rail: { paddingTop: spacing.lg, gap: spacing.md, width: 72, alignItems: 'center' },
  railItem: { alignItems: 'center', gap: 4 },
  railCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  railLabel: { ...typography.caption, fontWeight: '600' },
  railMenu: { position: 'absolute', left: 76, top: 8, zIndex: 5 },
  menuItem: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  timerMenuRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  timerMenuIcon: {
    fontSize: 16,
    lineHeight: 22,
    ...Platform.select({
      web: {
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
      },
      default: {},
    }),
  },
  menuPad: { paddingHorizontal: spacing.xs },
  dragGhost: {
    position: 'absolute',
    left: spacing.xl,
    right: 90,
  },
});
